import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal, FlatList, ActivityIndicator } from 'react-native';
import Unicon from '@/components/General/Unicon';
import { useUnistyles } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import SolidButton from '@/components/General/SolidButton';

const MoveModal = ({ visible, onClose, onMove, scope, ownerID, excludeFolderID }) => {
  const { theme: { colors } } = useUnistyles();
  const [selectedFolderID, setSelectedFolderID] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set());

  const { data: folderTree, isLoading } = Query.files.tree({ scope, ownerID });

  const toggleFolder = (folderID) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderID)) {
      newExpanded.delete(folderID);
    } else {
      newExpanded.add(folderID);
    }
    setExpandedFolders(newExpanded);
  };

  const buildTree = (folders, parentID = null) => {
    return folders
      .filter(f => f.parentFolderID === parentID && f.id !== excludeFolderID)
      .map(folder => ({
        ...folder,
        children: buildTree(folders, folder.id)
      }));
  };

  const renderFolder = (folder, level = 0) => {
    const hasChildren = folder.children && folder.children.length > 0;
    const isSelected = selectedFolderID === folder.id;
    const isExpanded = expandedFolders.has(folder.id);

    return (
      <View key={folder.id}>
        <TouchableOpacity
          onPress={() => setSelectedFolderID(folder.id)}
          onLongPress={() => hasChildren && toggleFolder(folder.id)}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            paddingVertical: 8,
            paddingLeft: 16 + level * 16,
            backgroundColor: isSelected ? colors.primary + '20' : 'transparent'
          }}
        >
          {hasChildren ? (
            <TouchableOpacity onPress={() => toggleFolder(folder.id)} style={{ marginRight: 8 }}>
              <Unicon
                name={isExpanded ? 'chevron-down' : 'chevron-right'}
                size={20}
                colorVariant="textSecondary"
                library="material"
              />
            </TouchableOpacity>
          ) : (
            <View style={{ width: 28 }} />
          )}

          <Unicon name="folder" size={20} colorVariant={isSelected ? 'primary' : 'textSecondary'} library="material" style={{ marginRight: 8 }} />
          <Text style={{ color: colors.text, fontSize: 14, flex: 1 }} numberOfLines={1}>
            {folder.name}
          </Text>
        </TouchableOpacity>

        {hasChildren && isExpanded && folder.children.map(child => renderFolder(child, level + 1))}
      </View>
    );
  };

  const tree = folderTree ? buildTree(folderTree, null) : [];

  const handleSubmit = () => {
    if (selectedFolderID) {
      onMove?.(selectedFolderID);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <View style={{ backgroundColor: colors.card, borderRadius: 12, padding: 20, width: '100%', maxWidth: 400, maxHeight: '80%' }}>
          <Text style={{ color: colors.text, fontSize: 18, fontWeight: '600', marginBottom: 16 }}>
            Move to...
          </Text>

          {isLoading ? (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <ActivityIndicator size="large" color={colors.primary} />
            </View>
          ) : (
            <FlatList
              data={tree}
              keyExtractor={(item) => item.id}
              renderItem={({ item }) => renderFolder(item)}
              ListEmptyComponent={
                <Text style={{ color: colors.textSecondary, textAlign: 'center', padding: 20 }}>
                  No folders available
                </Text>
              }
              style={{ maxHeight: 300, marginBottom: 16 }}
            />
          )}

          <View style={{ flexDirection: 'row', gap: 12, justifyContent: 'flex-end' }}>
            <SolidButton text="Cancel" type="textSecondary" variant="outlined" onPress={onClose} />
            <SolidButton text="Move" onPress={handleSubmit} disabled={!selectedFolderID} />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default MoveModal;
